import { useState } from 'react';
import { Button, Stack, TextField } from '@mui/material';
import ApprovalIcon from '@mui/icons-material/Check';
import type { CardFile } from '@serb/shared';
import { useAction } from '../../lib/useAction';
import { AttachmentPicker } from './AttachmentPicker';
import type { Approval } from './sample';
import type { EmailFields } from './useApprovals';

type Props = {
  a: Approval;
  onApprove: (fields: EmailFields) => Promise<void>;
  onCancel: () => void;
};

/** Change the email yourself, then approve your version. Your signature is added when it is sent. */
export function EmailEditor({ a, onApprove, onCancel }: Props) {
  const [to, setTo] = useState(a.to ?? '');
  const [cc, setCc] = useState(a.cc ?? '');
  const [subject, setSubject] = useState(a.subject ?? '');
  const [body, setBody] = useState(a.body ?? '');
  const [files, setFiles] = useState<CardFile[]>(a.files ?? []);
  const approve = useAction(() => onApprove({ to, cc, subject, body, attachments: files.map((f) => f.path) }), {
    success: 'Approved with your changes. The server is sending it now.',
  });

  return (
    <Stack spacing={1.5}>
      <TextField
        label="To"
        size="small"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        helperText="Separate addresses with a comma."
      />
      <TextField label="CC" size="small" value={cc} onChange={(e) => setCc(e.target.value)} />
      <TextField label="Subject" size="small" value={subject} onChange={(e) => setSubject(e.target.value)} />
      <TextField
        label="Message"
        multiline
        minRows={8}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        helperText="Your signature is added automatically. Do not type one."
      />
      <AttachmentPicker files={files} onChange={setFiles} code={a.srb} />
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<ApprovalIcon />}
          disabled={approve.busy}
          onClick={() => approve.run(undefined)}
        >
          Approve My Version
        </Button>
      </Stack>
    </Stack>
  );
}
